import React from 'react';
import PropTypes from 'prop-types';
import { startCase, sumBy } from 'lodash';
import { DetailsListLayoutMode, SelectionMode, Text } from 'office-ui-fabric-react';
import formatNumber from '../../utils/formatNumber';
import DetailsList from '../DetailsList';
import styles from './StatementTieringAndFeeStructure.style';

const TieringInterval = props => {
  const tieringIntervalColumns = [
    {
      key: 'tier',
      name: 'Tier',
      fieldName: 'tierId',
    },
    {
      key: 'threshold',
      name: 'Threshold',
      fieldName: 'threshold',
    },
    {
      key: 'rate',
      name: 'Rate',
      fieldName: 'rate',
    },
    {
      key: `current${props.tieringMethodType}`,
      name: `Current ${props.tieringMethodType}`,
      fieldName: 'currentTieringUnits',
    },
    {
      key: `interval${props.tieringMethodType}`,
      name: props.hasResetIntervals
        ? `Interval ${props.tieringMethodType}`
        : `CTD ${props.tieringMethodType}`,
      fieldName: 'intervalTieringUnits',
    },
    {
      key: `remaining${props.tieringMethodType}`,
      name: `Remaining ${props.tieringMethodType}`,
      fieldName: 'remainingUnits',
    },
  ];

  let items = [];
  let previousThreshold = 0;
  let minRateDecimals = 2;
  props.tiers.forEach(tier => {
    if (typeof tier.rate !== 'number' || !`${tier.rate}`.includes('.')) return;
    minRateDecimals = Math.max(minRateDecimals, `${tier.rate}`.match(/\.\d+/)[0].length - 1);
  });
  props.tiers.forEach(tier => {
    items.push({
      ...tier,
      rate:
        tier.rate === 'multiple'
          ? 'Multiple'
          : formatNumber(styles, tier.rate, 16, minRateDecimals),
      currentTieringUnits: formatNumber(styles, tier.currentTieringAmount, 2),
      intervalTieringUnits: formatNumber(styles, tier.intervalTieringAmount, 2),
      threshold: tier.threshold ? formatNumber(styles, tier.threshold, 2) : 'MAX',
      remainingUnits: tier.threshold
        ? formatNumber(styles, tier.threshold - (tier.intervalTieringAmount + previousThreshold), 2)
        : 'MAX',
    });
    previousThreshold = tier.threshold;
  });
  items = items.sort((a, b) => (a.tierId > b.tierId ? 1 : -1));

  items.push({
    currentTieringUnits: formatNumber(
      styles,
      sumBy(props.tiers, t => t.currentTieringAmount),
      2,
    ),
    intervalTieringUnits: formatNumber(
      styles,
      sumBy(props.tiers, t => t.intervalTieringAmount),
      2,
    ),
  });

  const renderField = (item, index, column) => {
    if (!item[column.fieldName]) return <></>;

    return (
      <Text variant={index === items.length - 1 ? 'medium' : 'smallPlus'}>
        {item[column.fieldName]}
      </Text>
    );
  };

  const intervalStartDate = new Date(props.intervalStartDate);
  const intervalEndDate = new Date(props.intervalEndDate);
  // database value for end date is the first day of expiration (last date within period is the previous day)
  intervalEndDate.setDate(intervalEndDate.getDate() - 1);

  return (
    <div className={styles.tieringInterval}>
      {props.hasResetIntervals && (
        <Text>
          Interval Period:{' '}
          {intervalStartDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })}
          {' - '}
          {intervalEndDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })}
        </Text>
      )}
      <DetailsList
        className={styles.intervalList}
        items={items}
        columns={tieringIntervalColumns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.fixedColumns}
        onRenderItemColumn={renderField}
      />
    </div>
  );
};

TieringInterval.propTypes = {
  tieringMethodType: PropTypes.string,
  hasResetIntervals: PropTypes.bool,
  intervalStartDate: PropTypes.string,
  intervalEndDate: PropTypes.string,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      tierId: PropTypes.string,
      rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      threshold: PropTypes.number,
      currentUnits: PropTypes.number,
      intervalUnits: PropTypes.number,
    }),
  ).isRequired,
};

TieringInterval.defaultProps = {
  tieringMethodType: 'Units',
  hasResetIntervals: false,
  intervalStartDate: '',
  intervalEndDate: '',
};

const StatementTieringAndFeeStructure = props => {
  const hasResetIntervals = resetInterval => resetInterval !== 'contractToDate';
  const tieringResetStartDate = new Date(props.tieringResetStartDate);
  const tieringMethodType = props.tieringMethod.includes('Units') ? 'Units' : 'Revenue';

  return (
    <>
      <h5 className={styles.tieringHeader}>
        <Text>{props.agreementName}</Text>
      </h5>
      <div className={styles.tieringMetaData}>
        <Text>Tiering Method: {startCase(props.tieringMethod)}</Text>
        <Text>Fee Method: {startCase(props.calculationMethod)}</Text>
        {hasResetIntervals(props.tieringResetInterval) ? (
          <Text>
            Tiering Interval: {startCase(props.tieringResetInterval)}, starting on{' '}
            {tieringResetStartDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            })}
          </Text>
        ) : (
          <Text className={styles.hidden} />
        )}
      </div>
      {props.tieringIntervals.map(tieringInterval => (
        <TieringInterval
          key={tieringInterval.intervalId}
          hasResetIntervals={hasResetIntervals(props.tieringResetInterval)}
          tieringMethodType={tieringMethodType}
          {...tieringInterval}
        />
      ))}
    </>
  );
};

StatementTieringAndFeeStructure.propTypes = {
  agreementName: PropTypes.string.isRequired,
  calculationMethod: PropTypes.string.isRequired,
  tieringMethod: PropTypes.string.isRequired,
  tieringResetInterval: PropTypes.string,
  tieringResetStartDate: PropTypes.string,
  tieringIntervals: PropTypes.arrayOf(PropTypes.shape(TieringInterval.propTypes)),
};

StatementTieringAndFeeStructure.defaultProps = {
  tieringResetInterval: 'contractToDate',
  tieringResetStartDate: '',
  tieringIntervals: [],
};

export default StatementTieringAndFeeStructure;
